import React, { useEffect, useMemo, useState } from "react";
import { Button, Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import notifications from "../../../common/Components/Notifications/Notification";
import { listLooms, setSupervisorRound } from "../../../services/karkhanaApi";
import { T, useKT } from "../i18n/kt";
import SearchField from "../ui/SearchField";
import { loomMatches } from "../home/loomCodes";
import { serverText, SupervisorRow } from "./SupervisorSheets";

/**
 * The owner sets a supervisor's usual round: looms and/or whole loom groups. It is soft: those
 * looms show first on the supervisor's Daily work, and they can still enter any loom of the studio.
 */

export type Round = { set?: boolean; loomIds?: string[]; loomCodes?: string[]; loomGroups?: string[] } | null | undefined;

/** "N01, N02 + Shed A", "N01, N02, N03, N04 +6 + Shed A", or "Not set". */
export const roundSummary = (round: Round, t: T, max = 4): string => {
  const codes = round?.loomCodes || [];
  const groups = round?.loomGroups || [];
  if (!round?.set || (!codes.length && !groups.length && !(round.loomIds || []).length)) return t("Not set");
  const parts: string[] = [];
  const ids = round.loomIds || [];
  if (codes.length) {
    const more = codes.length - max;
    parts.push(codes.slice(0, max).join(", ") + (more > 0 ? ` ${t("+{{n}} more", { n: more })}` : ""));
  } else if (ids.length) {
    parts.push(ids.length === 1 ? t("1 loom") : t("{{n}} looms", { n: ids.length }));
  }
  groups.forEach((g) => parts.push(g));
  return parts.join(" + ");
};

/** Distinct loom groups, first spelling kept, in name order. */
export const loomGroupsOf = (looms: any[]): string[] => {
  const seen = new Map<string, string>();
  looms.forEach((l) => {
    const g = String(l.loomGroup || "").trim();
    if (g && !seen.has(g.toLowerCase())) seen.set(g.toLowerCase(), g);
  });
  return Array.from(seen.values()).sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
};

const ask = async (fn: () => Promise<any>): Promise<any> => {
  try {
    return (await fn()) || { success: false, message: "Something went wrong" };
  } catch {
    return { success: false, message: "Could not reach the server", offline: true };
  }
};

/** Only active looms that aren't retired can be in a round (the list sends `active` and `retired`). */
export const liveLoom = (l: any) => l && l.active !== false && !l.retired;

export const SupervisorRoundSheet = ({
  supervisor,
  onClose,
  onSaved,
}: {
  supervisor: SupervisorRow;
  onClose: () => void;
  onSaved: (row: SupervisorRow) => void;
}) => {
  const { t } = useKT();
  const [looms, setLooms] = useState<any[] | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [ids, setIds] = useState<Set<string>>(() => new Set(supervisor.round?.loomIds || []));
  const [groups, setGroups] = useState<Set<string>>(() => new Set((supervisor.round?.loomGroups || []).map((g: string) => g.toLowerCase())));
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Looms and groups of the saved round that are no longer in the studio, taken off so Save works.
  const [dropped, setDropped] = useState<string[]>([]);

  const load = () => {
    setLoadError(false);
    setLooms(null);
    ask(() => listLooms()).then((res) => {
      if (!res?.success) {
        setLoadError(true);
        return;
      }
      const all: any[] = res.data || [];
      const live = all.filter(liveLoom);
      const liveIds = new Set(live.map((l) => l.id));
      const liveGroups = new Set(loomGroupsOf(live).map((g) => g.toLowerCase()));
      const savedIds: string[] = supervisor.round?.loomIds || [];
      const savedCodes: string[] = supervisor.round?.loomCodes || [];
      const gone: string[] = [];
      savedIds.forEach((id, i) => {
        if (!liveIds.has(id)) gone.push(String(all.find((l) => l.id === id)?.code || savedCodes[i] || t("a loom")));
      });
      (supervisor.round?.loomGroups || []).forEach((g: string) => {
        if (!liveGroups.has(g.trim().toLowerCase())) gone.push(g);
      });
      setIds((cur) => new Set(Array.from(cur).filter((id) => liveIds.has(id))));
      setGroups((cur) => new Set(Array.from(cur).filter((g) => liveGroups.has(g))));
      setDropped(gone);
      setLooms(live);
    });
  };
  useEffect(load, [supervisor.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const allGroups = useMemo(() => loomGroupsOf(looms || []), [looms]);
  const shown = (looms || []).filter((l) => loomMatches(l, q));
  const inGroup = (l: any) => !!l.loomGroup && groups.has(String(l.loomGroup).trim().toLowerCase());

  const toggleLoom = (id: string) =>
    setIds((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  const toggleGroup = (g: string) =>
    setGroups((s) => {
      const n = new Set(s);
      const k = g.toLowerCase();
      if (n.has(k)) n.delete(k);
      else n.add(k);
      return n;
    });

  const save = async () => {
    if (!looms) return;
    setBusy(true);
    setError(null);
    // Only looms and groups that are still in the studio go back; a retired loom simply drops out.
    const body = {
      loomIds: looms.filter((l) => ids.has(l.id)).map((l) => l.id),
      loomGroups: allGroups.filter((g) => groups.has(g.toLowerCase())),
    };
    const res = await ask(() => setSupervisorRound(supervisor.id, body));
    setBusy(false);
    if (!res?.success) {
      setError(serverText(res, t, "Could not save"));
      return;
    }
    notifications({ title: t("Done"), message: t("{{name}}'s usual round is saved", { name: supervisor.name }), type: "success" });
    onSaved(res.data);
  };

  const picked = ids.size + groups.size;

  return (
    <Modal isOpen toggle={onClose} modalClassName="kw-modal">
      <ModalHeader toggle={onClose}>{t("{{name}}'s usual round", { name: supervisor.name })}</ModalHeader>
      <ModalBody>
        <p className="kw-muted" style={{ marginTop: 0 }}>
          {t("Their looms show first. They can still enter any loom when covering for someone.")}
        </p>
        {looms === null && !loadError ? <p className="kw-muted">{t("Loading…")}</p> : null}
        {dropped.length ? (
          <p className="kw-muted" role="status">
            {dropped.length === 1
              ? t("{{what}} is retired and was taken off this round. Save to keep the change.", { what: dropped[0] })
              : t("{{what}} are retired and were taken off this round. Save to keep the change.", { what: dropped.join(", ") })}
          </p>
        ) : null}
        {loadError ? (
          <div className="ksv-box is-warn" role="alert">
            {t("Could not load the looms")}{" "}
            <button type="button" className="ksv-link" onClick={load}>
              {t("Try again")}
            </button>
          </div>
        ) : null}
        {looms && !looms.length ? <p className="kw-muted">{t("No looms yet.")}</p> : null}
        {looms && looms.length ? (
          <>
            {allGroups.length ? (
              <>
                <h3 className="ksv-sub" style={{ marginTop: 0 }}>
                  {t("Loom groups")}
                </h3>
                <div className="ksv-groups" role="group" aria-label={t("Loom groups")}>
                  {allGroups.map((g) => (
                    <button
                      key={g}
                      type="button"
                      className={`kr-choice${groups.has(g.toLowerCase()) ? " is-on" : ""}`}
                      aria-pressed={groups.has(g.toLowerCase())}
                      onClick={() => toggleGroup(g)}
                    >
                      {g}
                    </button>
                  ))}
                </div>
              </>
            ) : null}
            <h3 className="ksv-sub">{t("Looms")}</h3>
            <SearchField value={q} onChange={setQ} placeholder={t("Find a loom, e.g. N05")} label={t("Find a loom")} autoCapitalize="characters" />
            {!shown.length ? <p className="kw-muted">{t("Nothing here for this filter.")}</p> : null}
            <ul className="ksv-looms">
              {shown.map((l) => (
                <li key={l.id}>
                  <label>
                    <input type="checkbox" checked={ids.has(l.id)} onChange={() => toggleLoom(l.id)} />
                    <span className="kw-code">{l.code}</span>
                    {l.loomGroup ? <span className="kw-muted">{l.loomGroup}</span> : null}
                    {inGroup(l) && !ids.has(l.id) ? <span className="kw-chip">{t("In a picked group")}</span> : null}
                  </label>
                </li>
              ))}
            </ul>
          </>
        ) : null}
        {error ? (
          <div className="ksv-box is-warn mt-3" role="alert">
            {error}
          </div>
        ) : null}
      </ModalBody>
      <ModalFooter>
        {picked ? (
          <Button
            color="link"
            className="me-auto mr-auto"
            onClick={() => {
              setIds(new Set());
              setGroups(new Set());
            }}
          >
            {t("Clear")}
          </Button>
        ) : null}
        <Button color="secondary" outline onClick={onClose}>
          {t("Cancel")}
        </Button>
        <Button className="ResetBtn" disabled={busy || !looms} onClick={save}>
          {busy ? t("Saving…") : picked ? t("Save round") : t("Save with no round")}
        </Button>
      </ModalFooter>
    </Modal>
  );
};
